import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, FileText, History, Lock, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

import { api, ApiError } from "@/api";
import { useAuth } from "@/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ListFilterBar from "@/components/ListFilterBar";
import DocumentNumberLink from "@/components/DocumentNumberLink";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import {
  isWithinDateRange,
  useClearFilterParams,
  useFilterParam,
} from "@/hooks/useFilterParam";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatNumber } from "@/lib/format";

interface ReceiptRow {
  id: number;
  receipt_number: string;
  quote_id: number | null;
  quote_number: string | null;
  customer_name: string;
  total_amount: number;
  amount_paid: number;
  balance: number;
  payment_method: string;
  payment_date: string | null;
  locked_by_waybill: boolean;
  // True only for GENERAL payments whose customer has had on-account credit
  // consumed by a waybill. Edits are blocked; cancellation stays available.
  locked_by_credit_application?: boolean;
  payment_source?: "cash" | "credit_application";
  reference_quote_number?: string | null;
  reference_quote_amount?: number | null;
  cheque_number?: string | null;
  is_general_payment?: boolean;
}

interface ReceiptCancellation {
  id: number;
  receipt_number: string;
  quote_number: string | null;
  customer_name: string;
  total_amount: number;
  amount_paid: number;
  balance: number;
  payment_method: string;
  payment_date: string | null;
  reference_quote_number?: string | null;
  reference_quote_amount?: number | null;
  cheque_number?: string | null;
  cancellation_reason: string;
  cancelled_by: string;
  cancelled_at: string | null;
}

function methodBadge(method: string) {
  const map: Record<string, string> = {
    Cash: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    "Mobile Money":
      "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30",
    "Bank Transfer":
      "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
    Cheque:
      "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
    Card: "bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/30",
  };
  return (
    map[method] ??
    "bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30"
  );
}

export default function Receipts() {
  const nav = useNavigate();
  const { can } = useAuth();
  const canCreate = can("receipts.create");
  const canEdit = can("receipts.write");
  const canCancel = can("receipts.cancel");
  const canRead = can("receipts.read");

  const [rows, setRows] = useState<ReceiptRow[]>([]);
  const [cancellations, setCancellations] = useState<ReceiptCancellation[]>([]);
  const [search, setSearch] = useFilterParam("q");
  const [docNo, setDocNo] = useFilterParam("doc_no");
  const [customerQuery, setCustomerQuery] = useFilterParam("customer");
  const [dateFrom, setDateFrom] = useFilterParam("date_from");
  const [dateTo, setDateTo] = useFilterParam("date_to");
  const [tab, setTab] = useState<"active" | "cancelled">("active");
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const clearFilters = useClearFilterParams([
    "q",
    "doc_no",
    "customer",
    "date_from",
    "date_to",
  ]);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ReceiptRow | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const customerOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) if (r.customer_name?.trim()) names.add(r.customer_name);
    for (const c of cancellations) {
      if (c.customer_name?.trim()) names.add(c.customer_name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows, cancellations]);

  const hasAnyFilter = Boolean(
    search || docNo || customerQuery || dateFrom || dateTo,
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const docNeedle = docNo.trim().toLowerCase();
    const custNeedle = customerQuery.trim().toLowerCase();
    return rows.filter((r) => {
      const number = r.receipt_number.toLowerCase();
      const cust = r.customer_name.toLowerCase();
      const ref = (r.quote_number || "").toLowerCase();
      const searchMatch =
        !s || number.includes(s) || cust.includes(s) || ref.includes(s);
      const docMatch = !docNeedle || number.includes(docNeedle);
      const custMatch = !custNeedle || cust.includes(custNeedle);
      const dateMatch = isWithinDateRange(r.payment_date, dateFrom, dateTo);
      return searchMatch && docMatch && custMatch && dateMatch;
    });
  }, [rows, search, docNo, customerQuery, dateFrom, dateTo]);

  const filteredCancelled = useMemo(() => {
    const s = search.trim().toLowerCase();
    const docNeedle = docNo.trim().toLowerCase();
    const custNeedle = customerQuery.trim().toLowerCase();
    return cancellations.filter((c) => {
      const number = c.receipt_number.toLowerCase();
      const cust = c.customer_name.toLowerCase();
      const ref = (c.quote_number || "").toLowerCase();
      const searchMatch =
        !s || number.includes(s) || cust.includes(s) || ref.includes(s);
      const docMatch = !docNeedle || number.includes(docNeedle);
      const custMatch = !custNeedle || cust.includes(custNeedle);
      const dateMatch = isWithinDateRange(c.payment_date, dateFrom, dateTo);
      return searchMatch && docMatch && custMatch && dateMatch;
    });
  }, [cancellations, search, docNo, customerQuery, dateFrom, dateTo]);

  const activePg = usePagination(filtered);
  const cancelledPg = usePagination(filteredCancelled);

  useEffect(() => {
    activePg.setPage(1);
    cancelledPg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, docNo, customerQuery, dateFrom, dateTo]);

  async function loadActive() {
    setLoading(true);
    try {
      const res = await api.get<{ receipts: ReceiptRow[] }>("/receipts");
      setRows(res.receipts ?? []);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load receipts");
    } finally {
      setLoading(false);
    }
  }

  async function loadCancellations() {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await api.get<{ cancellations: ReceiptCancellation[] }>(
        "/receipts/cancellations/history",
      );
      setCancellations(res.cancellations ?? []);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Failed to load cancellations";
      setHistoryError(msg);
      setCancellations([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    loadActive();
    loadCancellations();
  }, []);

  function openCancelDialog(r: ReceiptRow) {
    if (r.locked_by_waybill) {
      toast.error("This receipt is locked — a waybill exists for the quotation.");
      return;
    }
    setCancelTarget(r);
    setCancelReason("");
    setCancelOpen(true);
  }

  async function confirmCancel() {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) {
      toast.error("Please enter a reason for cancellation");
      return;
    }
    setCancelling(true);
    try {
      await api.del(`/receipts/${cancelTarget.id}`, {
        reason: cancelReason.trim(),
      });
      toast.success("Receipt cancelled — see Cancelled tab");
      setCancelOpen(false);
      setCancelTarget(null);
      setCancelReason("");
      setTab("cancelled");
      await Promise.all([loadActive(), loadCancellations()]);
    } catch (err) {
      toast.error((err as Error).message || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Receipts</h1>
          <p className="text-muted-foreground">
            Active receipts below. Cancelled receipts move to the Cancelled tab.
          </p>
        </div>
        {canCreate ? (
          <Button
            onClick={() => nav("/receipts/new")}
            className="bg-violet-500 hover:bg-violet-400 text-white rounded-xl px-5"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Receipt
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        <ListFilterBar
          title="Search Receipt"
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search receipts..."
          docNo={{
            value: docNo,
            onChange: setDocNo,
            placeholder: "Receipt No.",
          }}
          customer={{
            value: customerQuery,
            onChange: setCustomerQuery,
            options: customerOptions,
            placeholder: "Customer",
          }}
          dateFrom={{ value: dateFrom, onChange: setDateFrom }}
          dateTo={{ value: dateTo, onChange: setDateTo }}
          hasAnyFilter={hasAnyFilter}
          onClear={clearFilters}
        />
        <Card className="shadow-card">
          <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as "active" | "cancelled")}
          className="w-full"
        >
          <div className="px-4 pt-3 border-b">
            <TabsList>
              <TabsTrigger value="active">Active ({rows.length})</TabsTrigger>
              <TabsTrigger value="cancelled" className="gap-1.5">
                <History className="h-3.5 w-3.5" />
                Cancelled ({cancellations.length})
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="active" className="mt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Receipt No.</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-muted-foreground py-10"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : null}
                {!loading && filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-muted-foreground py-10"
                    >
                      No active receipts.
                      {cancellations.length > 0 ? (
                        <span className="block text-xs mt-1">
                          You have {cancellations.length} cancelled receipt(s) —
                          open the Cancelled tab.
                        </span>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ) : null}
                {!loading &&
                  activePg.pageItems.map((r, i) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-muted-foreground">
                        {activePg.from + i}
                      </TableCell>
                      <TableCell className="font-mono">
                        <div className="flex flex-col items-start gap-1">
                          <DocumentNumberLink
                            to={`/receipts/${r.id}`}
                            canOpen={canRead}
                          >
                            {r.receipt_number}
                          </DocumentNumberLink>
                          {r.is_general_payment ? (
                            <span className="inline-flex items-center rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300">
                              On account
                            </span>
                          ) : r.payment_source === "credit_application" ? (
                            <span className="inline-flex items-center rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300">
                              Paid from credit
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {r.customer_name || "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(r.amount_paid)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-0.5">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs ${methodBadge(r.payment_method)}`}
                          >
                            {r.payment_method}
                          </span>
                          {r.cheque_number && r.cheque_number.trim() ? (
                            <span className="font-mono text-[10px] text-muted-foreground">
                              #{r.cheque_number}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.payment_date
                          ? new Date(r.payment_date).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => nav(`/receipts/${r.id}?openPdf=1`)}
                            title="Preview PDF"
                          >
                            <FileText className="h-4 w-4 text-primary" />
                          </Button>
                          {!r.locked_by_waybill ? (
                            <>
                              {/* General payments whose pooled on-account
                                  credit has been applied to a waybill can no
                                  longer be edited, but stay cancellable. */}
                              {r.locked_by_credit_application ? (
                                <span
                                  className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-700 dark:text-amber-300"
                                  title="Locked — its on-account credit has been applied to a waybill. Editing is disabled; you can still cancel."
                                >
                                  <Lock className="h-3 w-3" />
                                  Locked
                                </span>
                              ) : canEdit ? (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() =>
                                    nav(`/receipts/${r.id}/edit`)
                                  }
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4 text-primary" />
                                </Button>
                              ) : null}
                              {canCancel ? (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => openCancelDialog(r)}
                                  title="Cancel receipt"
                                >
                                  <Ban className="h-4 w-4 text-destructive" />
                                </Button>
                              ) : null}
                            </>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-700 dark:text-amber-300">
                              <Lock className="h-3 w-3" />
                              Locked
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {!loading && filtered.length > 0 ? (
              <Pagination
                page={activePg.page}
                pageCount={activePg.pageCount}
                total={activePg.total}
                from={activePg.from}
                to={activePg.to}
                onPrev={activePg.prev}
                onNext={activePg.next}
              />
            ) : null}
          </TabsContent>

          <TabsContent value="cancelled" className="mt-0">
            {historyError ? (
              <div className="px-4 py-6 text-center text-sm text-destructive">
                {historyError}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 ml-3"
                  onClick={loadCancellations}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Receipt No.</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead>Cancelled By</TableHead>
                    <TableHead>Cancelled At</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center text-muted-foreground py-10"
                      >
                        Loading cancellation history...
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {!historyLoading && filteredCancelled.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center text-muted-foreground py-10"
                      >
                        No cancelled receipts yet.
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {!historyLoading &&
                    cancelledPg.pageItems.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono text-xs">
                          <DocumentNumberLink
                            to={`/receipts/cancelled/${c.id}`}
                            canOpen={canRead}
                          >
                            {c.receipt_number}
                          </DocumentNumberLink>
                        </TableCell>
                        <TableCell>{c.customer_name}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatNumber(c.amount_paid)}
                        </TableCell>
                        <TableCell>
                          {c.payment_date
                            ? new Date(c.payment_date).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell>{c.cancelled_by}</TableCell>
                        <TableCell>
                          {c.cancelled_at
                            ? new Date(c.cancelled_at).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell className="max-w-[280px] whitespace-pre-wrap text-sm">
                          {c.cancellation_reason}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() =>
                              nav(`/receipts/cancelled/${c.id}?openPdf=1`)
                            }
                            title="Preview cancelled receipt PDF"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
            {!historyError &&
            !historyLoading &&
            filteredCancelled.length > 0 ? (
              <Pagination
                page={cancelledPg.page}
                pageCount={cancelledPg.pageCount}
                total={cancelledPg.total}
                from={cancelledPg.from}
                to={cancelledPg.to}
                onPrev={cancelledPg.prev}
                onNext={cancelledPg.next}
              />
            ) : null}
          </TabsContent>
          </Tabs>
        </Card>
      </div>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Receipt</DialogTitle>
          </DialogHeader>
          {cancelTarget ? (
            <p className="text-sm text-muted-foreground">
              Cancelling{" "}
              <span className="font-mono text-foreground">
                {cancelTarget.receipt_number}
              </span>
              {cancelTarget.customer_name
                ? ` — ${cancelTarget.customer_name}`
                : ""}
              . It will be removed from Active and listed under the Cancelled
              tab.
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label>Reason for cancellation *</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              placeholder="Enter why this receipt is being cancelled..."
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelling}
            >
              Close
            </Button>
            <Button
              onClick={confirmCancel}
              disabled={cancelling}
              variant="destructive"
            >
              {cancelling ? "Cancelling..." : "Confirm Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
